import { inject, Injectable, TemplateRef } from '@angular/core';
import { DialogLike, DialogRef, LOADING_DIALOG_TOKEN } from '@pxl/models/common';
import { DialogService, LoaderOptions, LoadingService } from '@pxl/models/contracts';
import { promise_delay } from '@pxl/shared/util';
import { combineLatest, Observable, timer, catchError } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DefaultLoadingService implements LoadingService {
    private loading = false;

    private readonly dialogService = inject(DialogService);
    private readonly loadingDialogComponent = inject(LOADING_DIALOG_TOKEN);

    public async showWait<T>(promise: Promise<T>, opts?: LoaderOptions): Promise<T> {
        function getValuesOrDefaults(): Omit<LoaderOptions, 'showAfter'> & { showAfter: number } {
            const { label, showAfter, template } = opts ?? {};
            return { label, template, showAfter: showAfter ?? 500 };
        }

        if (this.loading) return promise;

        const { label, showAfter, template } = getValuesOrDefaults();

        let finishedLoading = false;
        await Promise.race([
            promise.then((data) => {
                finishedLoading = true;
                return data;
            }),
            promise_delay(showAfter),
        ]);
        if (finishedLoading) return promise;
        // show loading animation
        this.loading = true;

        const compRef = this.openLoaderComponent(template, label);

        let result: T;

        try {
            // wait for the task to finish.
            result = await promise;
        } finally {
            // stop loading animation and destroy component.
            compRef.instance.close();
            this.loading = false;
        }

        return result;
    }

    public showWaitObservable<T>(obs$: Observable<T>, opts?: LoaderOptions): Observable<T> {
        const { label, showAfter = 300, template } = opts ?? {};
        let compRef: DialogRef<DialogLike> | undefined;

        const showTemplate$ = timer(showAfter).pipe(
            map(() => {
                compRef = this.openLoaderComponent(template, label);
                return compRef;
            }),
        );

        return combineLatest([showTemplate$, obs$]).pipe(
            map(([_, obs]) => {
                compRef?.instance.close();
                return obs;
            }),
            catchError((err) => {
                compRef?.instance.close();
                throw err;
            }),
        );
    }

    private openLoaderComponent(template?: TemplateRef<unknown>, label?: string): DialogRef<DialogLike> {
        return this.dialogService.open(this.loadingDialogComponent, { template, label });
    }
}
